// =============================================================================
// Quantity & Price Step — Shopify-style quantity picker with line total
// =============================================================================

import type { OrderLineUpload, SelectedOptionEntry } from '@/types/order-wizard';
import { OptionUploadCards } from './OptionUploadCards';

interface QuantityPriceStepProps {
  currentQuantity: number;
  currentUnitPrice: number;
  selectedOptions?: SelectedOptionEntry[];
  optionSummary?: { label: string; value: string }[];
  uploads?: OrderLineUpload[];
  /** Set when the quantity was locked by a product-options submission. */
  quantityLocked?: boolean;
  priceUnavailable?: boolean;
  onQuantityChange: (qty: number) => void;
}

export function QuantityPriceStep({
  currentQuantity,
  currentUnitPrice,
  selectedOptions,
  optionSummary,
  uploads,
  quantityLocked = false,
  priceUnavailable = false,
  onQuantityChange,
}: QuantityPriceStepProps) {
  const unitPriceCents = Math.round(currentUnitPrice * 100);
  const lineTotal = (unitPriceCents * currentQuantity) / 100;
  const hasSurcharges = selectedOptions?.some((o) => o.surcharge > 0) ?? false;
  const totalSurchargeCents = selectedOptions?.reduce((sum, o) => sum + Math.round(o.surcharge * 100), 0) ?? 0;
  const basePrice = (unitPriceCents - totalSurchargeCents) / 100;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-[15px] font-semibold text-text-primary">
          Set Quantity
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          {quantityLocked
            ? 'Quantity was set with the product options.'
            : 'How many units would you like to order?'}
        </p>
      </div>

      {/* Selected Options Summary */}
      {((selectedOptions && selectedOptions.length > 0) || (optionSummary && optionSummary.length > 0) || (uploads && uploads.length > 0)) && (
        <div className="rounded-lg border border-border bg-surface-secondary p-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
            Selected Options
          </h4>
          {(selectedOptions?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {selectedOptions?.map((opt) => (
                <span key={opt.typeSlug} className="text-sm text-text-primary">
                  <span className="font-medium">{opt.typeName}:</span>{' '}
                  {opt.valueName}
                  {opt.surcharge > 0 && (
                    <span className="ml-1 text-xs text-text-tertiary">
                      (+${opt.surcharge.toFixed(2)})
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {(optionSummary?.length ?? 0) > 0 && (
            <div className="mt-1 flex flex-col gap-0.5">
              {optionSummary?.map((row) => (
                <p key={`${row.label}:${row.value}`} className="text-sm text-text-primary">
                  <span className="font-medium">{row.label}:</span> {row.value}
                </p>
              ))}
            </div>
          )}
          <OptionUploadCards uploads={uploads} />
        </div>
      )}

      {/* Quantity. Locked by the product-options submission, so the stepper stays hidden. */}
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-text-secondary">
          Quantity
        </label>
        {quantityLocked ? (
          <p className="text-sm font-semibold text-text-primary">{currentQuantity}</p>
        ) : (
        <div className="flex h-11 w-full max-w-[200px] items-center overflow-hidden rounded-lg border-2 border-border bg-white">
          <button
            type="button"
            className="flex h-full w-11 shrink-0 items-center justify-center text-text-secondary transition-colors hover:bg-surface-tertiary hover:text-text-primary disabled:opacity-30"
            onClick={() => onQuantityChange(Math.max(1, currentQuantity - 1))}
            disabled={currentQuantity <= 1}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
            </svg>
          </button>
          <input
            type="number"
            min={1}
            max={9999}
            value={currentQuantity}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val >= 1 && val <= 9999) onQuantityChange(val);
            }}
            className="h-full w-full border-x border-border bg-transparent text-center text-sm font-semibold text-text-primary outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <button
            type="button"
            className="flex h-full w-11 shrink-0 items-center justify-center text-text-secondary transition-colors hover:bg-surface-tertiary hover:text-text-primary disabled:opacity-30"
            onClick={() => onQuantityChange(Math.min(9999, currentQuantity + 1))}
            disabled={currentQuantity >= 9999}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        )}
      </div>

      {/* Price Breakdown */}
      <div className="rounded-lg border border-border bg-surface-secondary p-4">
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
          Price Breakdown
        </h4>
        <div className="space-y-1.5 text-sm">
          {priceUnavailable ? (
            <p className="font-medium text-warning-800">Price unavailable</p>
          ) : hasSurcharges ? (
            <>
              <div className="flex justify-between text-text-secondary">
                <span>Base cost</span>
                <span>${basePrice.toFixed(2)}</span>
              </div>
              {selectedOptions
                ?.filter((o) => o.surcharge > 0)
                .map((opt) => (
                  <div key={opt.typeSlug} className="flex justify-between text-text-secondary">
                    <span>+ {opt.typeName} ({opt.valueName})</span>
                    <span>${opt.surcharge.toFixed(2)}</span>
                  </div>
                ))}
              <div className="flex justify-between border-t border-border pt-1.5 font-semibold text-text-primary">
                <span>Unit price</span>
                <span>${currentUnitPrice.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between font-semibold text-text-primary">
              <span>Unit price</span>
              <span>${currentUnitPrice.toFixed(2)}</span>
            </div>
          )}
          {!priceUnavailable && (
            <>
              <div className="flex justify-between text-text-secondary">
                <span>Quantity</span>
                <span>&times; {currentQuantity}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-1.5 text-base font-bold text-text-primary">
                <span>Total</span>
                <span>${lineTotal.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}
